import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getErrorMessage } from "../../lib/errorMessage";
import { haversineMeters } from "../../lib/geo";
import { useGeolocation } from "../map/useGeolocation";
import { useCheckIn, useCheckInSummary, useCheckOut, useMyCheckIn } from "./hooks";

type Props = {
  station: { id: string; lat: number; lng: number };
};

export function CheckInButton({ station }: Props) {
  const { t } = useTranslation();
  const geo = useGeolocation();
  const myCheckIn = useMyCheckIn();
  const summary = useCheckInSummary(station.id);
  const checkInMut = useCheckIn();
  const checkOutMut = useCheckOut(station.id);
  const [duration, setDuration] = useState(50);

  const locationGranted = geo.status === "granted";
  const distance = locationGranted ? haversineMeters(geo.lat, geo.lng, station.lat, station.lng) : Infinity;
  // PRE-LAUNCH TESTING ONLY — revert to `distance <= 50` before public launch.
  // Backend radius is widened via app_settings.checkin_radius_m.
  const withinRange = distance <= 50_000_000;

  const active = myCheckIn.data;
  const isHere = active?.station_id === station.id && !active.checked_out_at;
  const isElsewhere = !!active && active.station_id !== station.id && !active.checked_out_at;

  const handleCheckIn = () => {
    if (!locationGranted) return;
    checkInMut.mutate({
      station_id: station.id,
      lat: geo.lat,
      lng: geo.lng,
      duration_minutes: duration,
    });
  };

  const handleCheckOut = () => {
    checkOutMut.mutate();
  };

  // Pick the most recently submitted mutation so a stale error from the previous
  // action doesn't shadow a fresh success on the other one.
  const latestMut = checkInMut.submittedAt > checkOutMut.submittedAt ? checkInMut : checkOutMut;
  const errorMsg = latestMut.error ? getErrorMessage(latestMut.error, t("checkin.error_generic")) : null;

  const summaryData = summary.data;
  const showSummary = summaryData && summaryData.active_count > 0;

  return (
    <div className="space-y-2">
      {showSummary && (
        <p className="text-sm text-volt-deep">
          {t("checkin.here_count", { count: summaryData.active_count })}
          {summaryData.next_leaving_at && (
            <span className="ml-1 text-volt">
              · {t("checkin.next_leaving", { time: formatTime(summaryData.next_leaving_at) })}
            </span>
          )}
        </p>
      )}

      {isHere ? (
        <button
          onClick={handleCheckOut}
          disabled={checkOutMut.isPending}
          className="w-full rounded-lg bg-ember/15 px-4 py-2.5 text-sm font-medium text-ember hover:bg-ember/25 disabled:opacity-50"
        >
          {checkOutMut.isPending ? t("checkin.checking_out") : t("checkin.check_out")}
        </button>
      ) : (
        <div className="space-y-2">
          {!isElsewhere && locationGranted && withinRange && (
            <div className="flex items-center gap-2">
              <label htmlFor="checkin-duration" className="text-xs text-fg-mute">
                {t("checkin.duration_label")}
              </label>
              <select
                id="checkin-duration"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="rounded border border-line px-2 py-1 text-xs"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={50}>50 min</option>
                <option value={60}>1h</option>
                <option value={90}>1h 30min</option>
              </select>
            </div>
          )}
          <button
            onClick={handleCheckIn}
            disabled={!locationGranted || !withinRange || isElsewhere || checkInMut.isPending}
            className="w-full rounded-lg bg-volt-dark px-4 py-2.5 text-sm font-medium text-fg hover:bg-volt-deep disabled:cursor-not-allowed disabled:bg-line-strong disabled:text-fg-mute"
          >
            {!locationGranted
              ? t("checkin.enable_location")
              : isElsewhere
                ? t("checkin.already_elsewhere")
                : !withinRange
                  ? t("checkin.too_far", { meters: Math.round(distance) })
                  : checkInMut.isPending
                    ? t("checkin.checking_in")
                    : t("checkin.check_in")}
          </button>
        </div>
      )}

      {errorMsg && <p className="text-xs text-ember">{errorMsg}</p>}
    </div>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
